// 과거 손익(Historical P&L) 표 입력 헬퍼 — 바닐라 dcf.js 의
// normalizeHistDate / cleanHistNum / handleHistPaste(엑셀 TSV 붙여넣기) 이식.
// DOM 을 만지지 않는 순수 함수만 두어 ProjectionTab 과 단위테스트가 공유한다.
import type { DcfHistRow } from "../types/dcf";

/** 표 열 순서 — 바닐라 HIST_COLS 와 동일 */
export const HIST_COLS = [
  "year_end",
  "revenue",
  "cogs",
  "sga",
  "depreciation",
] as const;

/** 바닐라 normalizeHistDate — 다양한 연도 표기를 YYYY-MM-DD 로 정규화 */
export function normalizeHistDate(raw: string): string {
  const t = (raw || "").trim();
  if (!t) return "";
  const m = t.match(/^(\d{4})[-/.\s]+(\d{1,2})[-/.\s]+(\d{1,2})$/);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  if (/^\d{8}$/.test(t)) return `${t.slice(0, 4)}-${t.slice(4, 6)}-${t.slice(6, 8)}`;
  if (/^\d{4}$/.test(t)) return `${t}-12-31`;
  return t;
}

/** 바닐라 cleanHistNum — 천단위 콤마·통화기호 등 제거 */
export function cleanHistNum(raw: string): string {
  return (raw || "")
    .trim()
    .replace(/,/g, "")
    .replace(/[^0-9.-]/g, "");
}

/** 클립보드 텍스트 → 셀 행렬.
 *  탭/줄바꿈이 없는 단일 값이면 null (브라우저 기본 붙여넣기에 맡긴다).
 *  여러 행인데 첫 행에 숫자 셀이 하나도 없으면 헤더로 보고 버린다 — 바닐라 handleHistPaste. */
export function parseHistClipboard(text: string): string[][] | null {
  if (!text.includes("\t") && !text.includes("\n")) return null;
  let matrix = text
    .replace(/\r/g, "")
    .replace(/\n+$/, "")
    .split("\n")
    .map((r) => r.split("\t"));
  if (matrix.length > 1) {
    const hasNum = matrix[0].some(
      (c) => cleanHistNum(c) !== "" && !isNaN(Number(cleanHistNum(c))),
    );
    if (!hasNum) matrix = matrix.slice(1);
  }
  return matrix;
}

function emptyRow(): DcfHistRow {
  return { year_end: "", revenue: 0, cogs: 0, sga: 0, depreciation: 0 };
}

/** 행렬을 (startRow, startCol) 기준으로 반영한 새 historical_pl 반환.
 *  부족한 행은 빈 행으로 자동 확장, HIST_COLS 범위를 넘는 열은 무시 — 바닐라 동일. */
export function applyHistPaste(
  rows: DcfHistRow[],
  startRow: number,
  startCol: number,
  matrix: string[][],
): DcfHistRow[] {
  const out = rows.map((r) => ({ ...r }));
  matrix.forEach((cells, ri) => {
    const rIdx = startRow + ri;
    while (out.length <= rIdx) out.push(emptyRow());
    cells.forEach((val, ci) => {
      const colIdx = startCol + ci;
      if (colIdx >= HIST_COLS.length) return;
      const key = HIST_COLS[colIdx];
      if (key === "year_end") out[rIdx].year_end = normalizeHistDate(val);
      else out[rIdx][key] = parseFloat(cleanHistNum(val)) || 0;
    });
  });
  return out;
}
